import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Order } from "types/Order";
import { fetchById } from "services/orders.service";

// Define the types for your initial state
interface orderContainerState {
  isLoading: boolean;
  isError: boolean;
  errMsg: string;
  orderEntity: Order | null;
  // The order id of the latest in-flight request. Responses (success or error)
  // only apply if they still match it, so a stale response for an order we've
  // navigated away from can't overwrite the current one or surface a wrong error.
  requestedOrderId: string | null;
}

// Define the initial state
const initialState: orderContainerState = {
  isLoading: true,
  isError: false,
  errMsg: "",
  orderEntity: null,
  requestedOrderId: null,
};

// Create the slice
const OrderContainerSlice = createSlice({
  name: "orderContainer",
  initialState,
  reducers: {
    setDestroy() {
      return initialState;
    },
    setLoading(state: orderContainerState, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    // Marks the start of a fetch: records which order is requested, shows
    // loading, and clears any prior error so a stale error can't block the new
    // order.
    setRequest(state: orderContainerState, action: PayloadAction<string>) {
      state.requestedOrderId = action.payload;
      state.isLoading = true;
      state.isError = false;
      state.errMsg = "";
    },
    setError(
      state: orderContainerState,
      action: PayloadAction<{ id: string; error: string }>
    ) {
      if (state.requestedOrderId !== action.payload.id) {
        return;
      }
      state.isLoading = false;
      state.isError = true;
      state.errMsg = action.payload.error;
    },
    setOrderContainer(
      state: orderContainerState,
      action: PayloadAction<{ id: string; data: any }>
    ) {
      if (state.requestedOrderId !== action.payload.id) {
        return;
      }
      state.orderEntity = action.payload.data;
      state.isLoading = false;
      state.isError = false;
      state.errMsg = "";
    },
  },
});

// Extract the action creators object and the reducer
const { actions, reducer } = OrderContainerSlice;

// Extract and export each action creator by name
export const { setDestroy, setLoading, setRequest, setError, setOrderContainer } =
  actions;

// Export the reducer, either as a default or named export
export default reducer;

// Define the type for the dispatch function
export const fetchOrderDetails =
  ({ orderId, companyId }: { orderId: string; companyId: string }) =>
  async (dispatch: any) => {
    dispatch(setRequest(orderId));
    try {
      const { orderEntity } = await fetchById(orderId, companyId);
      dispatch(setOrderContainer({ id: orderId, data: orderEntity }));
    } catch (e) {
      const error = "not_fetch";
      dispatch(setError({ id: orderId, error }));
      return false;
    }
  };
